/**
 * useConversationsQuery.ts — fetch + keyset-pagination state for the
 * Conversations list (CONV7). The conversations sibling of `useLeadsQuery`.
 *
 * Owns the request lifecycle for `GET /api/conversations`: the first page (a
 * fresh filter `load`), subsequent `loadMore` pages appended via the opaque
 * `next_cursor` (KEYSET — the cursor advances; NO offset/page-number), and the
 * four first-class UI phases (loading / ready / unauthorized / error). It also
 * surfaces the envelope's `detail_completeness` honesty flag so the page can
 * render the degraded-data note.
 *
 * Race-safety mirrors useLeadsQuery: a monotonic request id means only the
 * LATEST load/loadMore may commit, and a response resolving after unmount is a
 * no-op. A 401/403 → `unauthorized` (the clean screen); any 502/network/5xx →
 * `error`, never a crash or a blank. A failed `loadMore` keeps the rows on screen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient, ConversationsQuery } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type { ConversationListItem, DetailCompleteness } from '../../api/types';

export type ConversationsPhase = 'loading' | 'ready' | 'unauthorized' | 'error';

export interface ConversationsQueryState {
  items: ConversationListItem[];
  nextCursor: string | null;
  approxTotal: number | null;
  /** Envelope honesty flag; drives the degraded-data note. Null until first load. */
  detailCompleteness: DetailCompleteness | null;
  phase: ConversationsPhase;
  loadingMore: boolean;
  /** Set when a load failed (transport / 502 / 5xx); drives the error banner. */
  errored: boolean;
  /** Run a fresh first-page query (replaces the current rows). */
  load: (query: ConversationsQuery) => Promise<void>;
  /** Append the next keyset page; no-op when there is no `next_cursor`. */
  loadMore: () => Promise<void>;
}

export const useConversationsQuery = (apiClient: ApiClient): ConversationsQueryState => {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [approxTotal, setApproxTotal] = useState<number | null>(null);
  const [detailCompleteness, setDetailCompleteness] = useState<DetailCompleteness | null>(null);
  const [phase, setPhase] = useState<ConversationsPhase>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errored, setErrored] = useState(false);

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);
  // The active filter set (sans cursor), so loadMore can advance from it.
  const lastQueryRef = useRef<ConversationsQuery>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(
    async (query: ConversationsQuery, { append }: { append: boolean }) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      if (append) setLoadingMore(true);
      else setPhase('loading');
      try {
        const envelope = await apiClient.getConversations(query);
        if (!isCurrent()) return;
        setItems((prev) => (append ? [...prev, ...envelope.items] : envelope.items));
        setNextCursor(envelope.next_cursor);
        setApproxTotal(envelope.approx_total);
        setDetailCompleteness(envelope.detail_completeness);
        setErrored(false);
        setPhase('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setPhase('unauthorized');
          return;
        }
        // BFF 502 (source unavailable) / network / 5xx. Keep already-loaded rows
        // on an append failure; flag the error so the page can banner it.
        setErrored(true);
        if (append) {
          setPhase('ready');
        } else {
          setItems([]);
          setNextCursor(null);
          setPhase('error');
        }
      } finally {
        if (append && isCurrent()) setLoadingMore(false);
      }
    },
    [apiClient],
  );

  const load = useCallback(
    async (query: ConversationsQuery) => {
      lastQueryRef.current = query;
      await run({ ...query, cursor: null }, { append: false });
    },
    [run],
  );

  const loadMore = useCallback(async () => {
    if (nextCursor == null) return;
    await run({ ...lastQueryRef.current, cursor: nextCursor }, { append: true });
  }, [nextCursor, run]);

  return {
    items,
    nextCursor,
    approxTotal,
    detailCompleteness,
    phase,
    loadingMore,
    errored,
    load,
    loadMore,
  };
};
